import React, { useCallback, useContext, useEffect} from 'react';
import {useDispatch, useSelector} from "react-redux";
import { fetchHelp } from "../../actions/common/HelpActions";
import MarkdownRenderer from '../common/markdown/MarkdownRenderer';
import {AppState} from "../../reducers/RootReducer";
import ClosableDialog from "../common/dialogs/ClosableDialog";
import {IconButton, Tooltip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {LoadingHandler} from "../common/LoadingHandler";
import {LanguageContext} from "../../config/ContextConfig";
import {useVisibility} from "../../config/hooks/useToggle";
import HelpIcon from '@mui/icons-material/Help';

export interface HelpViewProps  {

}


const useStyles = makeStyles( theme => ({
    root: {
        width: 90+'%'
    },
    notification: { textAlign: "right" },
}));

/**
 * Renders a Help View component responsible for displaying contextual help information
 * for the current application view in a modal dialog.
 *
 * This component enhances the user experience by providing guidance and support
 * tailored to the current view, accessible through a help icon button. The content
 * is dynamically fetched based on the application's language and view context.
 *
 * @param props - The properties passed to the `HelpView` component. This includes
 * any configuration or data necessary for rendering the component.
 *
 * @return A React element that includes a help icon button and a modal dialog.
 * The help content is fetched as needed and rendered inside the modal.
 *
 * @throws Will throw an error if the `LanguageContext` or Redux state is not properly
 * provided or the necessary state properties are not initialized.
 */
function HelpView( props: HelpViewProps ){
    const helpState = useSelector( (state: AppState ) => state.HelpReducer );
    const [ isVisible, { toggleVisibility, hide } ] = useVisibility( false );
    const classes = useStyles();
    const dispatch = useDispatch();
    const fetchHelpForView = useCallback(
        ( viewIdentifier: string ) => dispatch( fetchHelp( viewIdentifier ) ),
        [ dispatch ]
    );
    const language = useContext( LanguageContext );

    const helpLanguageIdentifier = `${helpState.currentView}_${ language.languageIdentifier }`;

    useEffect( () => {
        if(  ! ( helpLanguageIdentifier in helpState.entries ) && ! helpState.request.isFetching && isVisible ) {
            fetchHelpForView( helpState.currentView );
        }
    });


//console.log(help);
        return (
            <>
                <Tooltip title={ language.help.title }>
                    <IconButton color="inherit" className={classes.notification} onClick={ toggleVisibility }>
                        <HelpIcon />
                    </IconButton>
                </Tooltip>

                <LoadingHandler isLoading={ ! ( helpLanguageIdentifier in helpState.entries ) && ! helpState.request.isFetching && helpState.isViewable }>
                    <ClosableDialog
                        fullWidth
                        onCloseClick={ hide }
                        isOpen={ isVisible }
                        title={ language.help.title }
                        dialogActionComponents={null}
                        className={classes.root}
                    >
                        <div style={{ overflowY: "scroll" }}>
                            <MarkdownRenderer source={helpState.entries[helpLanguageIdentifier]?.helpText ?? ""}/>
                        </div>
                    </ClosableDialog>
                </LoadingHandler>
            </>

        );

};

export default HelpView;